/* @img-migrated */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Calendar, UsersRound, Megaphone, Eye, Send, Loader2, CheckCircle2,
  Clock, RefreshCw, Filter, X, Sparkles, ChevronRight, AlertCircle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import ModalPortal from '../../components/ModalPortal';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  { id: 'events',        label: 'Events',        Icon: Calendar,  accent: '#E63946', desc: 'New events, recaps, and important happenings.' },
  { id: 'membership',    label: 'Membership',    Icon: UsersRound, accent: '#002249', desc: 'New members, updates, and meetings.' },
  { id: 'communication', label: 'Communication', Icon: Megaphone,  accent: '#457B9D', desc: 'Weekly news, blog posts, and announcements.' },
];

const fmtCountdown = (iso) => {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'in progress';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h >= 24) return `in ${Math.floor(h / 24)}d ${h % 24}h`;
  return `in ${h}h ${m}m`;
};

const AdminNewsletterCenter = () => {
  const [activeTab, setActiveTab] = useState('events');
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('all'); // For communication tab: all | blogs | external | announcements
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);

  // Lock body scroll while preview modal is open
  useBodyScrollLock(previewOpen);

  const fetchStatus = async () => {
    setLoadingStatus(true);
    try {
      const [statusRes, histRes] = await Promise.all([
        axios.get(`${API}/admin/newsletter-center/status`, { withCredentials: true }),
        axios.get(`${API}/admin/newsletter-center/history`, { withCredentials: true }),
      ]);
      setStatus(statusRes.data);
      setHistory(histRes.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load status');
    } finally {
      setLoadingStatus(false);
    }
  };

  const fetchItems = async (dept) => {
    setLoadingItems(true);
    try {
      const res = await axios.get(`${API}/admin/newsletter-center/available`, {
        params: { department: dept },
        withCredentials: true,
      });
      setItems(res.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not load ${dept} content`);
      setItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);
  useEffect(() => { fetchItems(activeTab); }, [activeTab]);

  const canContribute = (dept) => status?.user_departments?.includes(dept);

  const toggleMark = async (item) => {
    if (!canContribute(activeTab)) {
      toast.error('You do not have permission to contribute to this department');
      return;
    }
    try {
      if (item.is_marked) {
        // Find by listing contributions for this week+dept and item_ref_id
        const list = await axios.get(`${API}/admin/newsletter-center/contributions`, {
          params: { department: activeTab },
          withCredentials: true,
        });
        const match = (list.data?.contributions || []).find(
          (c) => c.item_type === item.item_type && c.item_ref_id === item.item_ref_id,
        );
        if (match) {
          await axios.delete(`${API}/admin/newsletter-center/contributions/${match.id}`, { withCredentials: true });
          toast.success('Removed from this week');
        }
      } else {
        await axios.post(`${API}/admin/newsletter-center/contributions`, {
          department: activeTab,
          item_type: item.item_type,
          item_ref_id: item.item_ref_id,
        }, { withCredentials: true });
        toast.success('Added to this week\'s newsletter');
      }
      // Refresh items + counts.
      await Promise.all([fetchItems(activeTab), fetchStatus()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update');
    }
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/admin/newsletter-center/preview`, { withCredentials: true });
      setPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load preview');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const generateNow = async (send = false) => {
    if (send && !window.confirm('Send this week\'s newsletter to all subscribers right now?')) return;
    setGenerating(true);
    try {
      await axios.post(`${API}/admin/newsletter-center/generate`, { send, audience: 'all' }, { withCredentials: true });
      toast.success(send ? 'Newsletter sent' : 'Draft created in Newsletters');
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not generate');
    } finally {
      setGenerating(false);
    }
  };

  const filtered = useMemo(() => {
    let list = items;
    if (activeTab === 'communication' && kindFilter !== 'all') {
      list = list.filter((it) => (it.category_kind || '') === kindFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((it) => (it.title || '').toLowerCase().includes(q) || (it.subtitle || '').toLowerCase().includes(q));
    }
    return list;
  }, [items, search, kindFilter, activeTab]);

  // Reset kind filter when tab changes
  useEffect(() => { setKindFilter('all'); }, [activeTab]);

  // Counts by kind for the Communication chips
  const commKindCounts = useMemo(() => {
    if (activeTab !== 'communication') return { all: 0, blogs: 0, external: 0, announcements: 0 };
    return items.reduce((acc, it) => {
      acc.all += 1;
      const k = it.category_kind;
      if (k && acc[k] !== undefined) acc[k] += 1;
      return acc;
    }, { all: 0, blogs: 0, external: 0, announcements: 0 });
  }, [items, activeTab]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="admin-newsletter-center">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#002249]/10 text-[#002249] text-[10px] font-bold uppercase tracking-wider mb-2">
            <Mail className="w-3 h-3" /> Newsletter Center · Weekly Digest
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#002249]">DCCF Weekly Digest</h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Each department selects content for the next Wednesday-7 AM digest. Empty sections auto-fill with the latest live content.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={fetchStatus} className="h-10 rounded-lg" data-testid="nc-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button onClick={openPreview} className="h-10 rounded-lg bg-[#002249] hover:bg-[#0a3570] text-white" data-testid="nc-preview-btn">
            <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview This Week
          </Button>
          <Button onClick={() => generateNow(false)} disabled={generating} variant="outline" className="h-10 rounded-lg" data-testid="nc-generate-draft">
            {generating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />} Draft Now
          </Button>
          <Button onClick={() => generateNow(true)} disabled={generating} className="h-10 rounded-lg bg-[#E63946] hover:bg-[#c02a35] text-white" data-testid="nc-send-now">
            <Send className="w-3.5 h-3.5 mr-1.5" /> Send Now
          </Button>
        </div>
      </div>

      {/* Status strip */}
      <StatusStrip status={status} loading={loadingStatus} />

      {/* Tabs */}
      <div className="bg-white border border-slate-200 rounded-xl mt-4 overflow-hidden">
        <div className="flex flex-col sm:flex-row border-b border-slate-200" data-testid="nc-tabs">
          {TABS.map((t) => {
            const active = activeTab === t.id;
            const count = status?.counts?.[t.id] ?? 0;
            const can = canContribute(t.id);
            const Icon = t.Icon;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                disabled={!can}
                className={`flex-1 px-4 py-4 text-left transition-colors relative ${active ? 'bg-slate-50' : 'hover:bg-slate-50'} ${!can ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid={`nc-tab-${t.id}`}
              >
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${t.accent}1a`, color: t.accent }}>
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <div className="min-w-0">
                    <p className={`text-[13px] font-bold ${active ? 'text-[#002249]' : 'text-slate-700'}`}>{t.label}</p>
                    <p className="text-[10.5px] text-slate-500 truncate">{t.desc}</p>
                  </div>
                  <span className={`ml-auto inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[10.5px] font-bold ${count > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-testid={`nc-tab-count-${t.id}`}>
                    {count}
                  </span>
                </div>
                {active && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: t.accent }} />}
                {!can && <span className="absolute top-1.5 right-1.5 text-[9.5px] font-bold text-slate-400 uppercase">No access</span>}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="p-3 border-b border-slate-200 bg-slate-50/40">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${activeTab} content...`}
              className="h-9 pl-9 rounded-lg"
              data-testid="nc-search"
            />
          </div>
        </div>

        {/* Kind chip strip — only for Communication tab */}
        {activeTab === 'communication' && (
          <div className="px-3 pt-3 pb-1 flex items-center gap-1.5 flex-wrap border-b border-slate-100" data-testid="nc-comm-kind-filter">
            {[
              { id: 'all',           label: 'All',            color: '#002249' },
              { id: 'blogs',         label: 'Blogs',          color: '#457B9D' },
              { id: 'external',      label: 'External News',  color: '#6B7280' },
              { id: 'announcements', label: 'Announcements',  color: '#A38600' },
            ].map((k) => {
              const active = kindFilter === k.id;
              const count = commKindCounts[k.id] ?? 0;
              return (
                <button
                  key={k.id}
                  onClick={() => setKindFilter(k.id)}
                  className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11.5px] font-semibold transition-all ${
                    active
                      ? 'text-white shadow-sm'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                  style={active ? { backgroundColor: k.color, borderColor: k.color } : {}}
                  data-testid={`nc-kind-${k.id}`}
                >
                  {k.label}
                  <span className={`inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Items list */}
        <div className="p-3" data-testid="nc-items-list">
          {loadingItems ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-slate-100 animate-pulse rounded-lg" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm" data-testid="nc-empty-state">
              {search
                ? `No ${activeTab} content matches "${search}".`
                : `No ${activeTab} content available right now. The system will auto-fill from the latest live content if you skip this section.`}
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((it) => (
                <ItemRow key={`${it.item_type}-${it.item_ref_id}`} item={it} onToggle={() => toggleMark(it)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl mt-4 p-4" data-testid="nc-history">
          <h3 className="text-[13.5px] font-bold text-[#002249] mb-2">Recent weekly digests</h3>
          <div className="space-y-1.5">
            {history.slice(0, 6).map((h) => (
              <div key={h.id} className="flex items-center justify-between text-[12.5px] py-1.5 border-b border-slate-100 last:border-0" data-testid={`nc-history-${h.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    h.status === 'sent' ? 'bg-emerald-100 text-emerald-700' :
                    h.status === 'failed' ? 'bg-rose-100 text-rose-700' :
                    h.status === 'scheduled' ? 'bg-sky-100 text-sky-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>{h.status}</span>
                  <span className="text-[#002249] font-semibold truncate">{h.subject}</span>
                </div>
                <span className="text-slate-400 text-[11px] font-mono ml-3 flex-shrink-0">{h.week_label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Preview drawer */}
      <AnimatePresence>
        {previewOpen && (
          <PreviewDrawer
            preview={preview}
            loading={previewLoading}
            onClose={() => { setPreviewOpen(false); setPreview(null); }}
            onSend={() => { setPreviewOpen(false); generateNow(true); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const StatusStrip = ({ status, loading }) => {
  if (loading || !status) return <div className="bg-white border border-slate-200 rounded-xl h-20 animate-pulse" />;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="nc-status-strip">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center flex-shrink-0">
            <Clock className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400">Cutoff</p>
            <p className="text-[14px] font-bold text-[#002249]" data-testid="nc-cutoff-label">
              Wed 7:00 AM ET <span className="text-slate-500 font-normal">· {fmtCountdown(status.cutoff_at_utc)}</span>
            </p>
            <p className="text-[11px] text-slate-500 truncate">{status.week_label} · key <span className="font-mono">{status.week_key}</span></p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 w-full sm:w-auto">
          {TABS.map((t) => (
            <div key={t.id} className="px-2 py-1.5 border border-slate-200 rounded-md min-w-0" data-testid={`nc-summary-${t.id}`}>
              <p className="text-[10.5px] font-bold uppercase tracking-wider truncate" style={{ color: t.accent }}>{t.label}</p>
              <p className="text-lg font-bold text-[#002249] leading-none mt-0.5">{status.counts?.[t.id] ?? 0} <span className="text-[10.5px] text-slate-400 font-medium">marked</span></p>
            </div>
          ))}
        </div>
      </div>
      {status.last_sent && (
        <p className="text-[11px] text-slate-400 mt-2">
          Last sent: <span className="text-slate-600">{status.last_sent.subject}</span> on{' '}
          <span className="font-mono">{status.last_sent.sent_at ? new Date(status.last_sent.sent_at).toLocaleString() : '—'}</span>
        </p>
      )}
    </div>
  );
};

const ItemRow = ({ item, onToggle }) => (
  <div
    className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${
      item.is_marked ? 'bg-emerald-50/40 border-emerald-200' : 'bg-white border-slate-200 hover:border-slate-300'
    }`}
    data-testid={`nc-item-${item.item_ref_id}`}
  >
    <div className="w-12 h-12 rounded-md bg-slate-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
      {item.image_url ? <Img variant="generic" src={resolveImg(item.image_url)} alt="" className="w-full h-full object-cover" loading="lazy" /> : <Mail className="w-4 h-4 text-slate-300" />}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[13.5px] font-semibold text-[#002249] truncate" title={item.title}>{item.title || 'Untitled'}</p>
      <p className="text-[11px] text-slate-500 truncate">
        <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[9.5px] font-bold uppercase tracking-wider mr-1.5">{item.kind}</span>
        {item.subtitle}
      </p>
    </div>
    <button
      onClick={onToggle}
      className={`h-8 px-3 rounded-md text-[12px] font-semibold transition-colors flex-shrink-0 ${
        item.is_marked
          ? 'bg-emerald-600 text-white hover:bg-emerald-700'
          : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-[#002249]'
      }`}
      data-testid={`nc-item-toggle-${item.item_ref_id}`}
    >
      {item.is_marked ? <><CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />Included</> : 'Add to digest'}
    </button>
  </div>
);

const PreviewDrawer = ({ preview, loading, onClose, onSend }) => (
  <ModalPortal>
    <motion.div
      className="fixed inset-0 z-[200] bg-slate-900/50 backdrop-blur-sm flex items-stretch justify-end"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        initial={{ x: 80, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 60, opacity: 0 }}
        transition={{ type: 'spring', damping: 24 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-2xl h-full overflow-hidden flex flex-col"
        data-testid="nc-preview-drawer"
      >
        <div className="px-5 py-3.5 bg-[#002249] text-white flex items-center justify-between flex-shrink-0">
          <div className="min-w-0">
            <p className="text-[10.5px] font-bold uppercase tracking-wider text-white/60">Preview</p>
            <h2 className="text-[15.5px] font-bold tracking-tight truncate">{preview?.subject || 'Preview this week'}</h2>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-md hover:bg-white/10 flex items-center justify-center" data-testid="nc-preview-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-100">
          {loading || !preview ? (
            <div className="flex items-center justify-center h-40 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading preview…
            </div>
          ) : (
            <iframe
              title="Newsletter preview"
              srcDoc={preview.body_html}
              className="w-full h-full border-0 bg-white"
              sandbox="allow-same-origin"
              data-testid="nc-preview-frame"
            />
          )}
        </div>

        {preview && (
          <div className="px-5 py-3 bg-white border-t border-slate-200 flex items-center justify-between flex-shrink-0" data-testid="nc-preview-footer">
            <div className="text-[11px] text-slate-500 flex items-center gap-2">
              <AlertCircle className="w-3 h-3" />
              <span>
                {preview.total_items} items ·
                {Object.entries(preview.autofilled).filter(([, n]) => n > 0).map(([k, n]) => ` ${n} ${k} auto-filled`).join(' ·') || ' no auto-fill needed'}
              </span>
            </div>
            <Button onClick={onSend} className="h-9 rounded-md bg-[#E63946] hover:bg-[#c02a35] text-white" data-testid="nc-preview-send">
              <Send className="w-3.5 h-3.5 mr-1.5" /> Send Now
            </Button>
          </div>
        )}
      </motion.div>
    </motion.div>
  </ModalPortal>
);

export default AdminNewsletterCenter;
